import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChecks, Loader2, PlayCircle } from "lucide-react";
import { canApproveProposal } from "@ckgo2/shared/types";
import { api, type ApiResponse } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import ProposalCard from "./ProposalCard";
import type { AgentPlan } from "./types";

type StepOutcome = "executed" | "failed";

interface ApproveAllResult {
  executed: Array<{ id: string }>;
  held: Array<{ id: string }>;
  failed: Array<{ id: string; error?: string }>;
  skipped: Array<{ id: string }>;
}

/**
 * A multi-step plan (Block 4.7): the ≥2 proposals one turn raised, as one ordered checklist.
 * "Approve all" runs the internal (write) steps now and HOLDS every outgoing/money step for an
 * explicit per-step confirmation — the hybrid gate. Held steps stay interactive as normal cards
 * (the operator confirms, and may Edit the email text first); executed/failed steps are reflected
 * via each card's forcedStatus so the panel never re-fetches.
 */
export default function PlanPanel({ plan }: { plan: AgentPlan }) {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role ?? "readonly");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, { status: StepOutcome; error?: string }>>(
    {},
  );
  const [ranAll, setRanAll] = useState(false);

  const hasInternal = plan.steps.some((s) => s.tier === "write");
  const canRunInternal = hasInternal && canApproveProposal("write", role);
  const heldCount = plan.steps.filter((s) => s.tier === "money").length;

  const approveAll = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ApiResponse<ApproveAllResult>>(
        `/api/admin/agent/plans/${plan.id}/approve`,
      );
      const map: Record<string, { status: StepOutcome; error?: string }> = {};
      for (const e of res.data.executed) map[e.id] = { status: "executed" };
      for (const f of res.data.failed) map[f.id] = { status: "failed", error: f.error };
      setOutcomes(map);
      setRanAll(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("shared.agent.approvePlanFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-primary-200 bg-primary-50/40 dark:border-primary-900/50 dark:bg-primary-900/10 rounded-xl border p-2.5">
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <div className="text-primary-700 dark:text-primary-300 flex items-center gap-1.5 text-xs font-semibold">
          <ListChecks className="h-4 w-4" />
          {plan.title}
        </div>
        {!ranAll && canRunInternal && (
          <button
            onClick={approveAll}
            disabled={busy}
            className="bg-primary-600 hover:bg-primary-700 inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <PlayCircle className="h-3.5 w-3.5" />
            )}
            {t("shared.agent.approveAll")}
          </button>
        )}
      </div>

      {ranAll && heldCount > 0 && (
        <p className="mb-2 px-1 text-[11px] text-amber-700 dark:text-amber-300">
          {t("shared.agent.heldNotice", { count: heldCount })}
        </p>
      )}
      {error && <p className="mb-2 px-1 text-xs text-red-600">{error}</p>}

      <div className="space-y-2">
        {plan.steps.map((s) => (
          <ProposalCard
            key={s.id}
            proposal={s}
            stepNumber={s.step}
            editable={s.editable}
            forcedStatus={outcomes[s.id]?.status}
            forcedError={outcomes[s.id]?.error}
          />
        ))}
      </div>
    </div>
  );
}
